import { useCallback, useEffect, useRef, useState } from 'react'
import DailyIframe, {
  type DailyCall,
  type DailyEventObject,
  type DailyEventObjectParticipant,
  type DailyEventObjectTrack,
  type DailyParticipant
} from '@daily-co/daily-js'

interface UseDailyRoomOptions {
  /** Disparado uma única vez quando o paciente entra na sala (joined-meeting). */
  onConnected?: () => void
  /** Disparado quando a saída local foi concluída (left-meeting após leaveRoom). */
  onDisconnected?: () => void
  /** Disparado quando o médico (participante remoto) sai da sala. */
  onRemoteEnded?: () => void
  /** Disparado em erros fatais de conexão (event 'error'). camera-error NÃO chama isto. */
  onError?: (message: string) => void
}

/**
 * Ciclo de vida da sala Daily.co (lado paciente / viewer).
 *
 * Padrões obrigatórios (migração real do assistant-cannect):
 * - participant-joined + participant-updated + track-started tratados juntos (redundância por timing).
 * - camera-error NÃO aborta o join (fallback somente-áudio).
 * - call.leave() + call.destroy() em TODO caminho (saída normal, erro, beforeunload) — senão a câmera fica presa.
 * - Refs de vídeo em array (reaplica o track quando a <video> monta depois).
 * - <audio> remoto solto no body, guardado em array para limpeza.
 * - Flags useRef (connectionReportedRef / localEndRequestedRef / remoteFinishHandledRef) para distinguir
 *   "eu saí" de "fui desconectado" e evitar dupla finalização no polling.
 */
export function useDailyRoom(options: UseDailyRoomOptions = {}) {
  const { onConnected, onDisconnected, onRemoteEnded, onError } = options

  const callRef = useRef<DailyCall | null>(null)
  const joiningRef = useRef(false)

  // Tracks de mídia atuais (MediaStreamTrack puros — render via new MediaStream([track]) -> srcObject).
  const localVideoTrackRef = useRef<MediaStreamTrack | null>(null)
  const remoteVideoTrackRef = useRef<MediaStreamTrack | null>(null)

  // Refs em array: a <video> pode montar depois do track chegar, então reaplicamos.
  const localVideoRefs = useRef<HTMLVideoElement[]>([])
  const remoteVideoRefs = useRef<HTMLVideoElement[]>([])

  // <audio> remoto solto no body, guardado para limpeza (key = participant session_id).
  const remoteAudioEls = useRef<Map<string, HTMLAudioElement>>(new Map())

  // Flags de controle de fluxo.
  const connectionReportedRef = useRef(false) // onConnected disparado só uma vez
  const localEndRequestedRef = useRef(false) // a saída foi pedida por nós (leaveRoom)
  const remoteFinishHandledRef = useRef(false) // já tratamos a saída do médico (evita dupla finalização)
  const cleanedRef = useRef(false) // evita cleanup duplo

  const [joined, setJoined] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isCameraOff, setIsCameraOff] = useState(false)

  // ---- Render de vídeo (persistentTrack -> MediaStream -> srcObject) ----

  const applyTrackToRefs = useCallback((refs: HTMLVideoElement[], track: MediaStreamTrack | null) => {
    refs.forEach((el) => {
      if (!el) return
      /* eslint-disable no-param-reassign -- mutação intencional do elemento <video> do DOM */
      if (track) {
        el.srcObject = new MediaStream([track])
        const playPromise = el.play?.()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            /* autoplay pode ser bloqueado; ignorar */
          })
        }
      } else {
        el.srcObject = null
      }
      /* eslint-enable no-param-reassign */
    })
  }, [])

  const applyLocalVideo = useCallback(() => {
    // Remove refs desconectadas do DOM antes de aplicar.
    localVideoRefs.current = localVideoRefs.current.filter((el) => el && el.isConnected)
    applyTrackToRefs(localVideoRefs.current, localVideoTrackRef.current)
  }, [applyTrackToRefs])

  const applyRemoteVideo = useCallback(() => {
    remoteVideoRefs.current = remoteVideoRefs.current.filter((el) => el && el.isConnected)
    applyTrackToRefs(remoteVideoRefs.current, remoteVideoTrackRef.current)
  }, [applyTrackToRefs])

  const getLocalVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      if (element && !localVideoRefs.current.includes(element)) {
        localVideoRefs.current.push(element)
        applyLocalVideo()
      } else if (!element) {
        localVideoRefs.current = localVideoRefs.current.filter((el) => el && el.isConnected)
      }
    },
    [applyLocalVideo]
  )

  const getRemoteVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      if (element && !remoteVideoRefs.current.includes(element)) {
        remoteVideoRefs.current.push(element)
        applyRemoteVideo()
      } else if (!element) {
        remoteVideoRefs.current = remoteVideoRefs.current.filter((el) => el && el.isConnected)
      }
    },
    [applyRemoteVideo]
  )

  // ---- Áudio remoto (<audio> solto no body) ----

  const attachRemoteAudio = useCallback((sessionId: string, track: MediaStreamTrack | null) => {
    if (!track) {
      const existing = remoteAudioEls.current.get(sessionId)
      if (existing) {
        existing.srcObject = null
        existing.remove()
        remoteAudioEls.current.delete(sessionId)
      }
      return
    }

    let el = remoteAudioEls.current.get(sessionId)
    if (!el) {
      el = document.createElement('audio')
      el.autoplay = true
      el.setAttribute('playsinline', 'true')
      el.style.display = 'none'
      document.body.appendChild(el)
      remoteAudioEls.current.set(sessionId, el)
    }
    el.srcObject = new MediaStream([track])
    const playPromise = el.play?.()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* autoplay pode ser bloqueado; ignorar */
      })
    }
  }, [])

  const clearRemoteAudio = useCallback(() => {
    remoteAudioEls.current.forEach((el) => {
      // eslint-disable-next-line no-param-reassign -- limpeza intencional do <audio> do DOM
      el.srcObject = null
      el.remove()
    })
    remoteAudioEls.current.clear()
  }, [])

  // ---- Sincronização de participantes ----

  const syncLocalParticipant = useCallback(
    (participant: DailyParticipant | undefined) => {
      if (!participant) return
      const videoTrack = participant.tracks?.video?.persistentTrack ?? null
      localVideoTrackRef.current = videoTrack
      applyLocalVideo()

      const audioState = participant.tracks?.audio?.state
      const videoState = participant.tracks?.video?.state
      // 'off'/'blocked' => mutado/câmera desligada.
      setIsMuted(audioState === 'off' || audioState === 'blocked')
      setIsCameraOff(videoState === 'off' || videoState === 'blocked')
    },
    [applyLocalVideo]
  )

  const syncRemoteParticipant = useCallback(
    (participant: DailyParticipant | undefined) => {
      if (!participant || participant.local) return

      const videoTrack = participant.tracks?.video?.persistentTrack ?? null
      // Só reaplica se mudou (evita recriar MediaStream à toa).
      if (videoTrack !== remoteVideoTrackRef.current) {
        remoteVideoTrackRef.current = videoTrack
        applyRemoteVideo()
      }

      const audioTrack = participant.tracks?.audio?.persistentTrack ?? null
      attachRemoteAudio(participant.session_id, audioTrack)
    },
    [applyRemoteVideo, attachRemoteAudio]
  )

  // ---- Cleanup central (leave + destroy em TODO caminho) ----

  const performCleanup = useCallback(async () => {
    if (cleanedRef.current) return
    cleanedRef.current = true

    const call = callRef.current
    callRef.current = null

    if (call) {
      try {
        await call.leave()
      } catch {
        /* noop */
      }
      try {
        await call.destroy()
      } catch {
        /* noop */
      }
    }

    // Limpa elementos de vídeo locais/remotos.
    applyTrackToRefs(localVideoRefs.current, null)
    applyTrackToRefs(remoteVideoRefs.current, null)
    localVideoRefs.current = []
    remoteVideoRefs.current = []
    localVideoTrackRef.current = null
    remoteVideoTrackRef.current = null

    clearRemoteAudio()

    setJoined(false)
    setIsMuted(false)
    setIsCameraOff(false)
    joiningRef.current = false
  }, [applyTrackToRefs, clearRemoteAudio])

  // ---- Join ----

  const joinRoom = useCallback(
    async (url: string, token: string) => {
      if (joined || joiningRef.current || callRef.current) return
      joiningRef.current = true
      cleanedRef.current = false
      connectionReportedRef.current = false
      localEndRequestedRef.current = false
      remoteFinishHandledRef.current = false

      try {
        const call = DailyIframe.createCallObject({
          subscribeToTracksAutomatically: true
        })
        callRef.current = call

        call
          .on('joined-meeting', (ev?: DailyEventObjectParticipant) => {
            setJoined(true)
            syncLocalParticipant(ev?.participants?.local)
            // Médico pode já estar na sala ao entrarmos.
            Object.values(ev?.participants ?? {}).forEach((p) => {
              if (p && !p.local) syncRemoteParticipant(p)
            })
            if (!connectionReportedRef.current) {
              connectionReportedRef.current = true
              onConnected?.()
            }
          })
          .on('participant-joined', (ev?: DailyEventObjectParticipant) => {
            syncRemoteParticipant(ev?.participant)
          })
          .on('participant-updated', (ev?: DailyEventObjectParticipant) => {
            if (ev?.participant?.local) {
              syncLocalParticipant(ev.participant)
            } else {
              syncRemoteParticipant(ev?.participant)
            }
          })
          .on('track-started', (ev?: DailyEventObjectTrack) => {
            // Redundância proposital: alguns tracks só chegam aqui.
            if (ev?.participant?.local) {
              syncLocalParticipant(ev.participant)
            } else {
              syncRemoteParticipant(ev?.participant)
            }
          })
          .on('track-stopped', (ev?: DailyEventObjectTrack) => {
            const participant = ev?.participant
            if (!participant) return
            if (participant.local) {
              syncLocalParticipant(participant)
            } else {
              syncRemoteParticipant(participant)
            }
          })
          .on('participant-left', (ev?: DailyEventObjectParticipant) => {
            const participant = ev?.participant
            if (!participant || participant.local) return
            // Limpa mídia do médico.
            remoteVideoTrackRef.current = null
            applyRemoteVideo()
            attachRemoteAudio(participant.session_id, null)
            if (!remoteFinishHandledRef.current && !localEndRequestedRef.current) {
              remoteFinishHandledRef.current = true
              onRemoteEnded?.()
            }
          })
          .on('left-meeting', () => {
            // Saída concluída (intencional ou não). Cleanup garante destroy.
            const wasIntentional = localEndRequestedRef.current
            performCleanup().finally(() => {
              if (wasIntentional) onDisconnected?.()
            })
          })
          .on('camera-error', (ev?: DailyEventObject) => {
            // NÃO aborta o join — seguimos em fallback somente-áudio.
            setIsCameraOff(true)
            // eslint-disable-next-line no-console
            console.warn('Daily camera-error (fallback somente-áudio):', ev)
          })
          .on('error', (ev?: DailyEventObject) => {
            const message =
              (ev && typeof ev === 'object' && 'errorMsg' in ev && (ev as { errorMsg?: string }).errorMsg) ||
              'Erro de conexão na videochamada.'
            // eslint-disable-next-line no-console
            console.error('Daily error:', ev)
            onError?.(message)
            performCleanup()
          })

        await call.join({ url, token })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao entrar na sala Daily:', error)
        await performCleanup()
        throw error
      } finally {
        joiningRef.current = false
      }
    },
    [
      joined,
      syncLocalParticipant,
      syncRemoteParticipant,
      applyRemoteVideo,
      attachRemoteAudio,
      performCleanup,
      onConnected,
      onDisconnected,
      onRemoteEnded,
      onError
    ]
  )

  const leaveRoom = useCallback(async () => {
    // Marca como saída intencional ANTES do cleanup, para os handlers distinguirem.
    localEndRequestedRef.current = true
    remoteFinishHandledRef.current = true
    await performCleanup()
  }, [performCleanup])

  // ---- Controles ----

  const toggleAudio = useCallback(() => {
    const call = callRef.current
    if (!call) return
    const next = isMuted // se mutado, próximo estado = ligar
    call.setLocalAudio(next)
    setIsMuted(!next)
  }, [isMuted])

  const toggleVideo = useCallback(() => {
    const call = callRef.current
    if (!call) return
    const next = isCameraOff // se desligada, próximo = ligar
    call.setLocalVideo(next)
    setIsCameraOff(!next)
  }, [isCameraOff])

  // ---- beforeunload + unmount: destroy garantido ----

  useEffect(() => {
    const handleBeforeUnload = () => {
      const call = callRef.current
      if (call) {
        try {
          call.leave()
        } catch {
          /* noop */
        }
        try {
          call.destroy()
        } catch {
          /* noop */
        }
        callRef.current = null
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      performCleanup()
    }
  }, [performCleanup])

  return {
    joined,
    isMuted,
    isCameraOff,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    getLocalVideoRef,
    getRemoteVideoRef
  }
}
